// src/App.js
import React, { useEffect, useState } from 'react';
import { ThemeProvider, CssBaseline } from '@mui/material';
import Collapse from '@mui/material/Collapse';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import theme from './styles/theme';
import NavbarComponent from './components/Navbar/NavbarComponent';
import FooterComponent from './components/Footer/FooterComponent';
import SignupBanner from './components/Banner/SignupBanner';
import HomePage from './pages/Home';
import DropdownManagement from './pages/DropdownManagementPage';

// Custom hook for tracking page views
const usePageTracking = () => {
  const location = useLocation();

  useEffect(() => {
    // Check if the dataLayer and GTM are initialized
    if (window.dataLayer && typeof window.dataLayer.push === 'function') {
      // Pushing data to the GTM dataLayer with the new page path
      window.dataLayer.push({
        event: 'pageview',
        page: location.pathname + location.search,
      });
    } else {
      console.error('GTM dataLayer is not initialized');
    }
  }, [location]);
};

const RouteChangeTracker = () => {
  usePageTracking();
  return null;
};

const App = () => {
  // ✅ Safer iframe detection:
  // - window.top / window.parent access can throw in cross-origin iframe cases.
  // - Using window.self !== window.top is the common pattern,
  //   but we still guard with try/catch to avoid crashing the app.
  const isInIframe = () => {
    try {
      return window.self !== window.top;
    } catch (e) {
      // If we can't access window.top due to cross-origin restrictions,
      // assume we're in an iframe to prevent runtime crashes.
      return true;
    }
  };

  // ✅ Safe localStorage helpers:
  // In some environments (privacy mode, blocked storage, embedded contexts),
  // localStorage access can throw and prevent React from rendering.
  const safeLocalStorageGet = (key) => {
    try {
      return window.localStorage.getItem(key);
    } catch (e) {
      return null;
    }
  };

  const safeLocalStorageSet = (key, value) => {
    try {
      window.localStorage.setItem(key, value);
    } catch (e) {
      // Intentionally ignore. We still allow the UI to function
      // even if persistence isn't available.
    }
  };

  // Compute once per render to avoid repeated cross-origin checks
  const inIframe = isInIframe();

  // Persist dismissal to reduce annoyance/bounce
  const [bannerOpen, setBannerOpen] = useState(() => {
    // ✅ Use safe getter to avoid crashes in restricted contexts
    return safeLocalStorageGet('hideSignupBanner') !== '1';
  });

  const closeBanner = () => {
    // ✅ Use safe setter to avoid crashes in restricted contexts
    safeLocalStorageSet('hideSignupBanner', '1');
    setBannerOpen(false);
  };

  useEffect(() => {
    // Trigger a pageview event for the initial page load
    if (window.dataLayer && typeof window.dataLayer.push === 'function') {
      window.dataLayer.push({
        event: 'pageview',
        page: window.location.pathname + window.location.search,
      });
    }
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <RouteChangeTracker /> {/* Include the tracker here */}

        {!inIframe && <NavbarComponent />}

        {/* ✅ New banner: uses SignupBanner.jsx default copy (no message prop override) */}
        {!inIframe && (
          <Collapse in={bannerOpen} timeout={200}>
            <SignupBanner
              ctaLabel="Start free trial"
              ctaHref="https://app.utmsmartmanager.com/signup/"
              onClose={closeBanner}
            />
          </Collapse>
        )}

        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/manage-dropdowns" element={<DropdownManagement />} />
          {/* Add other routes as needed */}
        </Routes>

        {!inIframe && <FooterComponent />}
      </Router>
    </ThemeProvider>
  );
};

export default App;